/**
 * Slideshow A/B Testing Functionality
 * Handles A/B test variations in slideshow slides
 */

class SlideshowABTest {
  constructor() {
    this.init();
  }

  init() {
    // Initialize A/B tests on page load
    this.initializeABTests();
    
    // Listen for slide changes (for Flickity or custom carousel)
    document.addEventListener('DOMContentLoaded', () => {
      this.setupCarouselListeners();
    });
  }

  /**
   * Initialize all A/B tests on the page
   */
  initializeABTests() {
    const abTestContainers = document.querySelectorAll('.ab-test-container');
    
    abTestContainers.forEach(container => {
      const testId = container.dataset.abTestId;
      const defaultVariation = container.dataset.defaultVariation || 'a';
      
      // Check if we have a stored variation for this test
      const storedVariation = this.getStoredVariation(testId);
      
      if (storedVariation) {
        // Use stored variation
        this.showVariation(container, storedVariation);
      } else {
        // Randomly select variation (50/50 split)
        const selectedVariation = Math.random() < 0.5 ? 'a' : 'b';
        this.showVariation(container, selectedVariation);
        this.storeVariation(testId, selectedVariation);
      }
    });
  }

  /**
   * Show specific variation and hide the other
   */
  showVariation(container, variation) {
    const allVariations = container.querySelectorAll('.ab-test-variation');
    
    allVariations.forEach(varEl => {
      const varType = varEl.dataset.abVariation;
      if (varType === variation) {
        varEl.style.display = '';
      } else {
        varEl.style.display = 'none';
      }
    });
    
    // Update container data attribute
    container.dataset.currentVariation = variation;
  }

  /**
   * Get stored variation from localStorage
   */
  getStoredVariation(testId) {
    try {
      return localStorage.getItem(`ab_test_${testId}`);
    } catch (e) {
      console.warn('localStorage not available:', e);
      return null;
    }
  }

  /**
   * Store variation in localStorage
   */
  storeVariation(testId, variation) {
    try {
      localStorage.setItem(`ab_test_${testId}`, variation);
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }

  /**
   * Track A/B test view (integrate with analytics)
   */
  trackVariation(testId, variation) {
    // Google Analytics tracking
    if (typeof gtag !== 'undefined') {
      gtag('event', 'ab_test_view', {
        'event_category': 'A/B Testing',
        'event_label': `${testId}_${variation}`,
        'test_id': testId,
        'variation': variation
      });
    }
    
    // Meta Pixel tracking
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'ABTestView', {
        test_id: testId,
        variation: variation
      });
    }
    
    // Custom tracking callback
    if (window.onABTestView && typeof window.onABTestView === 'function') {
      window.onABTestView(testId, variation);
    }
  }

  /**
   * Setup listeners for carousel changes
   */
  setupCarouselListeners() {
    // For Flickity carousel (if used)
    const carousels = document.querySelectorAll('.slideshow__carousel, [data-flickity]');
    carousels.forEach(carousel => {
      if (carousel.flickity) {
        carousel.addEventListener('change.flickity', (event) => {
          this.handleSlideChange(event.target);
        });
      }
    });
    
    // For custom slideshow-carousel component
    const slideshowCarousels = document.querySelectorAll('slideshow-carousel');
    slideshowCarousels.forEach(carousel => {
      carousel.addEventListener('change', (event) => {
        this.handleSlideChange(event.target);
      });
    });
  }

  /**
   * Handle slide change events
   */
  handleSlideChange(carousel) {
    const activeSlide = carousel.querySelector('.slideshow__slide.is-selected, .Carousel__Cell.is-selected');
    if (activeSlide) {
      const abTestContainer = activeSlide.querySelector('.ab-test-container');
      if (abTestContainer) {
        const testId = abTestContainer.dataset.abTestId;
        const currentVariation = abTestContainer.dataset.currentVariation;
        this.trackVariation(testId, currentVariation);
      }
    }
  }

  /**
   * Manually switch variation (for testing purposes)
   */
  switchVariation(testId, variation) {
    const container = document.querySelector(`.ab-test-container[data-ab-test-id="${testId}"]`);
    if (container) {
      this.showVariation(container, variation);
      this.storeVariation(testId, variation);
      this.trackVariation(testId, variation);
    }
  }

  /**
   * Reset A/B test (clear stored variation)
   */
  resetTest(testId) {
    try {
      localStorage.removeItem(`ab_test_${testId}`);
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }

  /**
   * Reset all A/B tests
   */
  resetAllTests() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('ab_test_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }
}

// Initialize A/B testing
const slideshowABTest = new SlideshowABTest();

// Expose to window for manual control
window.SlideshowABTest = slideshowABTest;

// Debug functions (only for development)
if (window.location.search.includes('ab_debug=true')) {
  window.abTestDebug = {
    switch: (testId, variation) => slideshowABTest.switchVariation(testId, variation),
    reset: (testId) => slideshowABTest.resetTest(testId),
    resetAll: () => slideshowABTest.resetAllTests(),
    getVariation: (testId) => slideshowABTest.getStoredVariation(testId)
  };
  console.log('A/B Test Debug Mode Active. Use window.abTestDebug to control tests.');
}
